// library/run — the Library's interpreter: the module's ONE impure file.
// Performs each Effect against the ports (and the device store, for the
// browse order) and feeds the outcome back as a Msg.
//
// The content search is debounced here: the author is mid-word, so only the
// last free text typed within DEBOUNCE_MS is sent. A newer search cancels the
// pending one; an answer that still arrives late is dropped by `update` (it
// carries its free text).

import { exhaustSwitchCase, Result } from "@bilda/utils";
import type { PackagingPorts } from "../../app/ports/packaging.js";
import type { ResourcePorts } from "../../app/ports/resource.js";
import type { PortResult, WorkspacePorts } from "../../app/ports.js";
import {
    contentQueryOf,
    type WordHits,
} from "../../components/Search/contentSearch.js";
import type { Interpreter } from "../module.js";
import type { LibraryEffect } from "./effect.js";
import type { ItemContent, LoadedKind } from "./model.js";
import type { LibraryMsg } from "./msg.js";

export type LibraryDeps = {
    readonly ports: Pick<WorkspacePorts, "searchContent"> &
        Pick<ResourcePorts, "loadSource" | "loadFile"> &
        Pick<PackagingPorts, "loadExport">;
};

const DEBOUNCE_MS = 250;

const mapOk = async <A, B>(r: PortResult<A>, f: (a: A) => B): PortResult<B> =>
    Result.map(f)(await r);

export const mkRunLibrary = ({
    ports,
}: LibraryDeps): Interpreter<LibraryEffect, LibraryMsg> => {
    let pending: ReturnType<typeof setTimeout> | undefined;

    const read = (id: string, kind: LoadedKind): PortResult<ItemContent> => {
        switch (kind) {
            case "source":
                return mapOk(ports.loadSource(id), (source) => ({
                    _tag: "Item.Source",
                    source,
                }));
            case "package":
                return mapOk(ports.loadExport(id), (pkg) => ({
                    _tag: "Item.Package",
                    pkg,
                }));
            case "file":
                return mapOk(ports.loadFile(id), (file) => ({
                    _tag: "Item.File",
                    file,
                }));
        }
    };

    return (effect, dispatch) => {
        switch (effect.type) {
            case "search_content": {
                const { words } = effect;
                clearTimeout(pending);
                pending = setTimeout(async () => {
                    // One search per word (the server matches one phrase);
                    // the projection joins them. Any failure fails the read.
                    const answers = await Promise.all(
                        words.map((word) =>
                            mapOk(
                                ports.searchContent(
                                    contentQueryOf(word) ?? word,
                                ),
                                (result): WordHits => ({ word, result }),
                            ),
                        ),
                    );
                    const failed = answers.find(Result.isErr);
                    dispatch(
                        failed === undefined
                            ? {
                                  type: "content_found",
                                  words,
                                  answers: answers.flatMap((r) =>
                                      Result.isOk(r) ? [r.value] : [],
                                  ),
                              }
                            : {
                                  type: "content_failed",
                                  words,
                                  error: failed.error,
                              },
                    );
                }, DEBOUNCE_MS);
                return;
            }
            case "load_detail": {
                const { id, kind } = effect;
                void read(id, kind).then((r) =>
                    dispatch(
                        Result.isOk(r)
                            ? { type: "detail_loaded", id, content: r.value }
                            : { type: "detail_failed", id, error: r.error },
                    ),
                );
                return;
            }
            default:
                exhaustSwitchCase(effect);
        }
    };
};
